import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { Loader2, Send, Briefcase, Volume2, VolumeX } from 'lucide-react';
import { MicButton, useSpeaker } from '../voice/VoiceControls';

const plain = (t) => t.replace(/[#*_`>|]/g, '').replace(/\[(.*?)\]\(.*?\)/g, '$1');
import { useAuth } from '../../context/AuthContext';

export const AgentChat = ({ sessionId, onSent, onBlocked }) => {
  const { api } = useAuth();
  const [session, setSession] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const speak = useSpeaker();
  const [autoVoice, setAutoVoice] = useState(() => localStorage.getItem('agentAutoVoice') !== 'off');
  const toggleVoice = () => { localStorage.setItem('agentAutoVoice', autoVoice ? 'off' : 'on'); setAutoVoice(!autoVoice); };

  useEffect(() => {
    api.get(`/specialists/sessions/${sessionId}`).then((r) => setSession(r.data)).catch(() => toast.error('No se pudo abrir la conversación'));
  }, [api, sessionId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [session?.messages?.length, sending]);

  const send = async (e, spoken) => {
    e?.preventDefault();
    const msg = (spoken ?? text).trim();
    if (!msg || sending) return;
    setText('');
    setSending(true);
    setSession((s) => ({ ...s, messages: [...s.messages, { role: 'user', text: msg }] }));
    try {
      const { data } = await api.post(`/specialists/sessions/${sessionId}/message`, { text: msg });
      setSession((s) => ({ ...s, messages: [...s.messages, data.message] }));
      if (autoVoice) speak(plain(data.message.text), true);
      onSent?.();
    } catch (err) {
      setSession((s) => ({ ...s, messages: s.messages.slice(0, -1) }));
      setText(msg);
      const d = err.response?.data?.detail;
      if (d?.code) onBlocked?.(d.code);
      else toast.error(d || 'El especialista no respondió');
    } finally {
      setSending(false);
    }
  };

  if (!session) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  return (
    <div className="flex h-[70vh] flex-col" data-testid="agent-chat">
      <div className="border-b border-slate-800 pb-3">
        <div className="flex items-center justify-between gap-2">
          <p data-testid="agent-chat-title" className="font-semibold text-white">Socio especialista en {session.area_name}</p>
          <button type="button" data-testid="agent-autovoice-toggle" aria-pressed={autoVoice} onClick={toggleVoice}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${autoVoice ? 'border-indigo-400 text-indigo-200' : 'border-slate-700 text-slate-400'}`}>
            {autoVoice ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />} Lectura automática {autoVoice ? 'activada' : 'desactivada'}
          </button>
        </div>
        {session.matter_title && (
          <p data-testid="agent-chat-matter" className="mt-1 inline-flex items-center gap-1 text-xs text-amber-300"><Briefcase className="h-3 w-3" /> Caso: {session.matter_title}</p>
        )}
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-1">
        {session.messages.length === 0 && (
          <p className="text-sm text-slate-400">Cuéntale tu consulta. {session.matter_title ? 'Ya leyó el caso del CRM y el análisis de Cerebro.' : 'Usará tus casos del CRM del área como contexto.'}</p>
        )}
        {session.messages.map((m, i) => (
          <div key={i} data-testid={`agent-msg-${m.role}-${i}`} className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm ${m.role === 'user' ? 'ml-auto bg-indigo-600 text-white' : 'bg-slate-800/80 text-slate-100'}`}>
            {m.role === 'user' ? m.text : <>
              <button type="button" data-testid={`agent-speak-${i}`} onClick={() => speak(plain(m.text))} className="float-right ml-2 text-slate-400 hover:text-indigo-300" title="Escuchar respuesta"><Volume2 className="h-4 w-4" /></button>
              <div className="space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:text-white [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold"><ReactMarkdown>{m.text}</ReactMarkdown></div></>}
          </div>
        ))}
        {sending && <div data-testid="agent-typing" className="inline-flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> El socio está analizando…</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-slate-800 pt-3">
        <textarea data-testid="agent-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e); }}
          placeholder="Escribe tu consulta…" className="flex-1 resize-none rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white focus:border-indigo-400 focus:outline-none" />
        <MicButton testid="agent-mic-btn" disabled={sending} className="self-end" onText={(t) => (autoVoice ? send(null, t) : setText((x) => (x ? `${x} ${t}` : t)))} />
        <button data-testid="agent-send-btn" disabled={sending || !text.trim()} className="self-end rounded-full bg-indigo-500 p-3 text-white hover:bg-indigo-400 transition-colors disabled:opacity-50"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
};
